import { useEffect } from 'react'

import useElementInBounds from 'src/lib/js/hooks/useElementInBounds'
import useScrollDirection from 'src/lib/js/hooks/useScrollDirection'
import { useIsMobile, ScreenWidth } from 'shared/js/utils/WindowContext'
import { usePopoverContext } from 'shared/components/common/popover/PopoverContext'
import { CART_POPOVER_CONTEXT_KEY } from 'public/components/default_template/online_ordering/cart/CartModal'
import { useGlobalNav } from 'public/components/default_template/GlobalNavContext'
import { useMenuSearchContext } from 'public/components/default_template/search/MenuSearchContext'

/**
 * Hook that manages scroll-based navigation visibility behavior for online ordering pages.
 *
 * Features:
 * - Creates a scroll ref that detects when user has scrolled past a threshold
 * - Automatically shows/hides global nav based on scroll direction
 * - Keeps nav visible when cart is open or using secondary nav
 * - Pauses behavior during menu search
 *
 * @returns An object containing:
 *   - scrollNavRef: Ref to attach to the scrollable content element
 *   - usingSecondaryNav: Boolean indicating if the secondary nav is in use (not scrolled past threshold)
 */
export const useScrollNavBehavior = () => {
  const { scrollDirection } = useScrollDirection()
  const cartPopoverContext = usePopoverContext(CART_POPOVER_CONTEXT_KEY)
  const [scrollNavRef, inBounds] = useElementInBounds<HTMLDivElement>({
    maxScrollY: 1
  })
  const isMobile = useIsMobile(ScreenWidth.SMALL)
  const { setGlobalNavOpen } = useGlobalNav()
  const usingSecondaryNav = inBounds
  const { isSearching } = useMenuSearchContext()

  useEffect(() => {
    if (!isSearching) {
      const shouldBeOpen =
        (isMobile ? scrollDirection === undefined : scrollDirection === 'up') ||
        cartPopoverContext?.isOpen ||
        !usingSecondaryNav
      setGlobalNavOpen(shouldBeOpen)
    }
  }, [
    scrollDirection,
    isMobile,
    cartPopoverContext?.isOpen,
    usingSecondaryNav,
    setGlobalNavOpen,
    isSearching
  ])

  return {
    scrollNavRef,
    usingSecondaryNav
  }
}
